import React, { useCallback, useState } from "react";
import Dialog from "~/components/shared/Dialog";
import Button from "~/components/shared/Button";
import Icon from "~/components/shared/Icon";
import classNames from "classnames";
import { useBuild } from "~/components/build/Show/lib/BuildContext";

const NEW_UI_FEATURES = [
  {
    icon: "custom/outline/devices",
    title: "Redesigned Layout",
    description:
      "The new layout aims to provide a more flexible workspace to visualise and understand your builds, allowing you to arrange the interface in a way that suits your workflow.",
  },
  {
    icon: "custom/outline/panel-right-bottom",
    title: "Slide-out Drawer",
    description:
      "Dive deep into your build details with a new slide-out drawer, accessible from anywhere in your build by clicking a step or job.",
  },
];

const EMAIL_TEMPLATE = {
  email: "support@buildkite.com",
  subject: encodeURIComponent("Feedback on the new build page experience"),
};

const PROVIDE_FEEDBACK_URL = `mailto:${EMAIL_TEMPLATE.email}?subject=${EMAIL_TEMPLATE.subject}`;

const NewBuildUIToggleDialog: React.FC<{ optIn: boolean }> = ({ optIn }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  if (optIn) {
    return (
      <>
        <OptInDialog isOpen={isDialogOpen} onRequestClose={closeDialog} />

        <button
          className="flex max-w-full items-center self-center truncate rounded-md border border-purple-200 bg-purple-100 px-2 py-1 font-medium text-purple-600 transition-colors hover:border-purple-300 hover:bg-purple-100 hover:text-purple-600"
          onClick={openDialog}
          data-testid="enable-new-build-ui"
        >
          <Icon
            icon="custom/outline/sparkle"
            className={classNames("mr-2 h-4 w-4", {
              "rotate-90": Features.BuildSidebar,
            })}
          />
          <span className="hidden max-w-full truncate lg:!block">Try the new build experience</span>
        </button>
      </>
    );
  }

  return (
    <>
      <OptOutDialog isOpen={isDialogOpen} onRequestClose={closeDialog} />

      <span
        data-active={isDialogOpen}
        role="button"
        className="group flex max-w-full items-center gap-1.5"
        onClick={openDialog}
        data-testid="disable-new-build-ui"
      >
        <span className="truncate rounded-sm border border-purple-300 bg-purple-100 px-1 py-0.5 text-xs text-purple-600">
          Preview
        </span>
        <span className="@lg/outlet:!inline hidden truncate text-xs hover:underline">
          Switch back
        </span>
      </span>
    </>
  );
};

interface DialogProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

export const OptInDialog = (props: DialogProps) => {
  const { build } = useBuild();
  const org = build.account.slug;

  return (
    <Dialog {...props} width={600}>
      <form className="p-8" method="POST" action={`/${org}/build_sidebar_enable`}>
        <input type="hidden" name={window._csrf.param} value={window._csrf.token} />
        <h2 className="text-charcoal-900 flex items-center text-2xl font-medium">
          <span>Try the new build page experience</span>
        </h2>
        <p className="text-charcoal-600 mb-6 text-base">
          A redesigned layout with more customization and flexible navigation.
        </p>
        <div className="mb-6 space-y-2">
          {NEW_UI_FEATURES.map((feature) => (
            <div key={feature.title} className="flex items-start space-x-4">
              <Icon icon={feature.icon} className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
              <div>
                <h3 className="text-charcoal-800 mb-0 mt-0.5 text-lg font-medium">
                  {feature.title}
                </h3>
                <p className="text-charcoal-400 mt-1">{feature.description}</p>
              </div>
            </div>
          ))}
          <div className="flex items-center space-x-3 rounded-md bg-purple-100 px-3 py-1 text-purple-600">
            <Icon icon="heroicons/outline/information-circle" className="opacity-90" />
            <div className="mb-2 text-sm">
              <p>
                <strong>Preview feature</strong>
              </p>
              This is an early preview of our new UI. Switch back to classic view at any time.
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 md:space-x-4">
          <Button
            type="button"
            onClick={props.onRequestClose}
            theme="default"
            className="rounded-md px-4 py-2 hover:bg-gray-200"
          >
            Maybe later
          </Button>
          <Button type="submit" theme="primary" className="px-4 py-2 font-medium transition-colors">
            Enable new UI preview
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export const OptOutDialog = (props: DialogProps) => {
  const { build } = useBuild();
  const org = build.account.slug;

  return (
    <Dialog {...props} width={600}>
      <form className="p-8" method="POST" action={`/${org}/build_sidebar_disable`}>
        <input type="hidden" name={window._csrf.param} value={window._csrf.token} />
        <h2 className="text-charcoal-900 flex items-center text-2xl font-medium">
          <span>Switch back to classic experience?</span>
        </h2>

        <p className="text-charcoal-600 mb-4 text-base">
          You&apos;re using the{" "}
          <a
            href="https://buildkite.com/docs/pipelines/build-page"
            target="_blank"
            rel="noreferrer"
            className="text-purple-600 underline hover:text-purple-600 focus:text-purple-600"
          >
            new build page
          </a>
          —which offers a redesigned layout with more flexibility, better navigation, and powerful
          features to help you understand and debug your builds faster.
        </p>

        <div className="mb-6">
          <p className="text-charcoal-800 mb-4 text-base font-medium">
            By switching back, you&apos;ll miss out on:
          </p>
          <div className="mb-4 space-y-2">
            {NEW_UI_FEATURES.map((feature) => (
              <div key={feature.title} className="flex items-start space-x-4">
                <Icon icon={feature.icon} className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
                <div>
                  <h3 className="text-charcoal-800 mb-0 mt-0.5 text-lg font-medium">
                    {feature.title}
                  </h3>
                  <p className="text-charcoal-400 mt-1">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6 space-y-2">
          <div className="flex items-center space-x-3 rounded-md bg-purple-100 px-3 py-1">
            <p className="text-charcoal-700 text-sm">
              💬 Want to help improve the experience?&nbsp;
              <a
                href={PROVIDE_FEEDBACK_URL}
                target="_blank"
                rel="noreferrer"
                className="text-purple-600 no-underline hover:text-purple-600 hover:!underline focus:text-purple-600"
              >
                <strong>Leave feedback</strong>
              </a>
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-2 md:space-x-4">
          <Button
            type="button"
            onClick={props.onRequestClose}
            theme="default"
            className="rounded-md px-4 py-2 hover:bg-gray-200"
          >
            Nevermind
          </Button>
          <Button
            type="submit"
            theme="primary"
            className="px-4 py-2 font-medium transition-colors"
            autoFocus={true}
          >
            Switch back
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default NewBuildUIToggleDialog;
